import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ThemeProvider } from "@aws-amplify/ui-react";
import {
  AwsCredentialProvider,
  FaceLivenessDetectorCore,
} from "@aws-amplify/ui-react-liveness";
import "@aws-amplify/ui-react/styles.css";
import { CustomButton } from "../../../../../core/components/CustomButton";
import { LogoTitleBlack } from "../../../../../core/components/LogoTitle";
import { apiServices, setToken } from "../../../../../core/utilities/ApiServices";
import {
  clearAccountRecoverySession,
  getAccountRecoveryToken,
} from "../../../../../core/utilities/accountRecovery";

type MissingItem =
  | "username"
  | "birth"
  | "gender"
  | "orientation"
  | "marital_status"
  | "desires"
  | "show_me"
  | "location"
  | "photo";

type Category = { id: string; name: string };
type RecoveryState = {
  missing_items: MissingItem[];
  first_missing_item: MissingItem | null;
  can_restore: boolean;
  likes_count: number;
  open_likes_you: boolean;
  categories: {
    genders: Category[];
    orientations: Category[];
    marital_statuses: Category[];
    desires: Category[];
    show_me: Category[];
  };
};

type RecoveryPhotoCheck = {
  requiresLiveness: boolean;
  shouldBlock: boolean;
  message?: string;
  contextToken?: string;
  session?: {
    sessionId: string;
    region: string;
    accessKeyId: string;
    secretAccessKey: string;
    sessionToken: string;
    expiresAt?: string | null;
  };
};

const normalizePhotoCheck = (data: any): RecoveryPhotoCheck => {
  const session = data?.liveness_session;
  const hasSession =
    typeof session?.session_id === "string" &&
    typeof session?.region === "string" &&
    typeof session?.access_key_id === "string" &&
    typeof session?.secret_access_key === "string" &&
    typeof session?.session_token === "string";
  return {
    requiresLiveness: data?.requires_liveness === true,
    shouldBlock: data?.should_block === true,
    message:
      typeof data?.message === "string" && data.message.trim()
        ? data.message.trim()
        : undefined,
    contextToken:
      typeof data?.context_token === "string" && data.context_token.trim()
        ? data.context_token.trim()
        : undefined,
    session: hasSession
      ? {
          sessionId: session.session_id,
          region: session.region,
          accessKeyId: session.access_key_id,
          secretAccessKey: session.secret_access_key,
          sessionToken: session.session_token,
          expiresAt: session.expires_at || null,
        }
      : undefined,
  };
};

const ITEM_TITLES: Record<MissingItem, string> = {
  username: "Choose your username",
  birth: "Confirm your birthday",
  gender: "Choose your gender",
  orientation: "Choose your orientation",
  marital_status: "Choose your relationship status",
  desires: "What are you looking for?",
  show_me: "Who would you like to see?",
  location: "Confirm your location",
  photo: "Add your profile photo",
};

const SelectField = ({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: Category[];
  onChange: (value: string) => void;
}) => (
  <label className="mt-5 flex w-full flex-col gap-2 text-sm font-semibold">
    {label}
    <select
      aria-label={label}
      className="rounded-lg border border-gray-300 bg-white px-4 py-3"
      value={value}
      onChange={(event) => onChange(event.target.value)}
    >
      <option value="">Choose one</option>
      {options.map((option) => (
        <option key={option.id} value={option.id}>
          {option.name}
        </option>
      ))}
    </select>
  </label>
);

export const AccountRecoveryScreen = () => {
  const navigate = useNavigate();
  const completionStarted = useRef(false);
  const [state, setState] = useState<RecoveryState | null>(null);
  const [value, setValue] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoCheck, setPhotoCheck] = useState<RecoveryPhotoCheck | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const livenessCredentials = useMemo<AwsCredentialProvider | undefined>(() => {
    const session = photoCheck?.session;
    if (!session) return undefined;
    return async () => ({
      accessKeyId: session.accessKeyId,
      secretAccessKey: session.secretAccessKey,
      sessionToken: session.sessionToken,
      expiration: session.expiresAt ? new Date(session.expiresAt) : undefined,
    });
  }, [photoCheck]);

  const finish = useCallback(async () => {
    if (completionStarted.current) return;
    completionStarted.current = true;
    try {
      const response = await apiServices.post("/account-recovery/complete");
      const normalToken = String(response.data?.token || "").trim();
      if (normalToken) {
        window.localStorage.setItem("userToken", normalToken);
        setToken(normalToken);
      }
      const openLikes = response.data?.open_likes_you === true;
      clearAccountRecoverySession();
      window.location.replace(openLikes ? "/notification" : "/");
    } catch {
      completionStarted.current = false;
      setError("Your recovery session expired. Please sign in again.");
    }
  }, []);

  const loadState = useCallback(async () => {
    const token =
      getAccountRecoveryToken() || window.localStorage.getItem("userToken") || "";
    if (!token) {
      navigate("/auth/login", { replace: true });
      return;
    }
    setToken(token);
    try {
      const response = await apiServices.get<RecoveryState>(
        "/account-recovery/state",
      );
      setState(response.data);
      if (response.data.can_restore) {
        await finish();
      }
    } catch {
      setError("Your recovery session expired. Please sign in again.");
    } finally {
      setLoading(false);
    }
  }, [finish, navigate]);

  useEffect(() => {
    void loadState();
  }, [loadState]);

  const saveItem = async (payload: Record<string, string>) => {
    setLoading(true);
    setError("");
    try {
      const response = await apiServices.put<RecoveryState>(
        "/account-recovery/item",
        payload,
      );
      setState(response.data);
      setValue("");
      setSelected([]);
      if (response.data.can_restore) await finish();
    } catch (requestError: any) {
      setError(
        requestError?.response?.data?.message ||
          "We could not save that detail. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const saveChoices = async () => {
    if (!state?.first_missing_item || selected.length === 0) {
      setError("Choose at least one option.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const response = await apiServices.put<RecoveryState>(
        "/account-recovery/choices",
        { [state.first_missing_item]: selected },
      );
      setState(response.data);
      setSelected([]);
      if (response.data.can_restore) await finish();
    } catch (requestError: any) {
      setError(
        requestError?.response?.data?.message ||
          "We could not save that detail. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const acceptNextState = async (nextState: RecoveryState) => {
    setState(nextState);
    setPhoto(null);
    setPhotoCheck(null);
    if (nextState.can_restore) await finish();
  };

  const uploadRecoveryPhoto = async (approvalToken?: string) => {
    if (!photo) {
      setError("Choose a profile photo to continue.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const formData = new FormData();
      formData.append("file", photo);
      if (approvalToken) formData.append("approval_token", approvalToken);
      const response = await apiServices.put<RecoveryState>(
        "/account-recovery/photo",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      await acceptNextState(response.data);
    } catch (requestError: any) {
      setError(
        requestError?.response?.data?.message ||
          "We could not save that photo. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const checkRecoveryPhoto = async () => {
    if (!photo) {
      setError("Choose a profile photo to continue.");
      return;
    }
    setLoading(true);
    setError("");
    setPhotoCheck(null);
    try {
      const formData = new FormData();
      formData.append("file", photo);
      const response = await apiServices.post(
        "/account-recovery/photo/liveness-check",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      const result = normalizePhotoCheck(response.data);
      if (result.shouldBlock) {
        setError(result.message || "Please choose a different profile photo.");
        return;
      }
      if (result.requiresLiveness) {
        if (!result.session || !result.contextToken) {
          setError("Live check details are missing. Please try again.");
          return;
        }
        setPhotoCheck(result);
        return;
      }
      await uploadRecoveryPhoto();
    } catch (requestError: any) {
      setError(
        requestError?.response?.data?.message ||
          "We could not check that photo. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const finishRecoveryLiveness = async () => {
    const session = photoCheck?.session;
    if (!session || !photoCheck.contextToken || !photo) {
      setError("Live check details are missing. Please choose the photo again.");
      setPhotoCheck(null);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const response = await apiServices.post(
        "/account-recovery/photo/liveness-result",
        {
          session_id: session.sessionId,
          context_token: photoCheck.contextToken,
        },
      );
      const status = String(response.data?.status || "").toLowerCase();
      const approvalToken = String(response.data?.approval_token || "").trim();
      if (status !== "passed" || !approvalToken) {
        setError(
          status === "expired"
            ? "Live check expired. Please try again."
            : "Live check could not confirm your face. Please try again.",
        );
        setPhotoCheck(null);
        return;
      }
      await uploadRecoveryPhoto(approvalToken);
    } catch (requestError: any) {
      setError(
        requestError?.response?.data?.message ||
          "Live check could not be completed. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const confirmLocation = () => {
    setError("");
    if (!navigator.geolocation) {
      setError("Location access is required to continue.");
      return;
    }
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = String(position.coords.latitude);
        const lng = String(position.coords.longitude);
        void saveItem({ lat, lng, country_lat: lat, country_lng: lng });
      },
      () => {
        setLoading(false);
        setError("Please allow location access to continue.");
      },
    );
  };

  const submitSingle = (event: FormEvent) => {
    event.preventDefault();
    if (!state?.first_missing_item || !value.trim()) {
      setError("Complete this detail to continue.");
      return;
    }
    const payloadKey: Partial<Record<MissingItem, string>> = {
      username: "username",
      birth: "birth",
      gender: "gender_id",
      orientation: "sexual_orientation_id",
      marital_status: "marital_status_id",
    };
    const key = payloadKey[state.first_missing_item];
    if (!key) return;
    void saveItem({ [key]: value.trim() });
  };

  if (loading) {
    return (
      <div className="flex h-svh items-center justify-center">Checking your account…</div>
    );
  }

  if (!state) {
    return (
      <main className="flex min-h-svh items-center justify-center bg-gray-50 px-5">
        <div className="max-w-md rounded-2xl bg-white p-7 text-center shadow-lg">
          <h1 className="text-2xl font-bold">Recovery could not continue</h1>
          <p className="mt-3 text-gray-600">
            {error || "Please sign in again to safely check this account."}
          </p>
          <CustomButton
            type="button"
            className="mt-6"
            onClick={() => navigate("/auth/login", { replace: true })}
          >
            Sign in again
          </CustomButton>
        </div>
      </main>
    );
  }

  const item = state.first_missing_item;
  const categoryOptions =
    item === "gender"
      ? state.categories.genders
      : item === "orientation"
        ? state.categories.orientations
        : item === "marital_status"
          ? state.categories.marital_statuses
          : [];
  const choiceOptions =
    item === "desires"
      ? state.categories.desires
      : item === "show_me"
        ? state.categories.show_me
        : [];

  return (
    <main className="min-h-svh bg-gray-50 px-5 py-8">
      <div className="mx-auto max-w-lg rounded-2xl bg-white p-7 shadow-lg">
        <LogoTitleBlack />
        <h1 className="mt-6 text-2xl font-bold">Recover your account</h1>
        <p className="mt-2 text-gray-600">
          We will ask only for details that are missing. Your existing profile,
          messages, matches and account benefits will stay unchanged.
        </p>

        {item ? <h2 className="mt-7 text-xl font-semibold">{ITEM_TITLES[item]}</h2> : null}

        {item === "username" || item === "birth" ? (
          <form onSubmit={submitSingle}>
            <label className="mt-5 flex w-full flex-col gap-2 text-sm font-semibold">
              {item === "birth" ? "Birthday" : "Username"}
              <input
                type={item === "birth" ? "date" : "text"}
                placeholder={item === "birth" ? "YYYY-MM-DD" : "Enter username"}
                className="rounded-lg border border-gray-300 px-4 py-3"
                value={value}
                onChange={(event) => setValue(event.target.value)}
              />
            </label>
            <CustomButton type="submit" className="mt-6" disabled={loading}>
              Continue
            </CustomButton>
          </form>
        ) : null}

        {item === "gender" || item === "orientation" || item === "marital_status" ? (
          <form onSubmit={submitSingle}>
            <SelectField
              label={ITEM_TITLES[item]}
              value={value}
              options={categoryOptions}
              onChange={setValue}
            />
            <CustomButton type="submit" className="mt-6" disabled={loading}>
              Continue
            </CustomButton>
          </form>
        ) : null}

        {item === "desires" || item === "show_me" ? (
          <div className="mt-5 space-y-3">
            {choiceOptions.map((option) => (
              <label key={option.id} className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={selected.includes(option.id)}
                  onChange={(event) =>
                    setSelected((current) =>
                      event.target.checked
                        ? [...current, option.id]
                        : current.filter((id) => id !== option.id),
                    )
                  }
                />
                {option.name}
              </label>
            ))}
            <CustomButton type="button" className="mt-6" onClick={saveChoices}>
              Continue
            </CustomButton>
          </div>
        ) : null}

        {item === "location" ? (
          <CustomButton type="button" className="mt-6" onClick={confirmLocation}>
            Allow location and continue
          </CustomButton>
        ) : null}

        {item === "photo" ? (
          <div className="mt-5">
            {photoCheck?.session && livenessCredentials ? (
              <div className="overflow-hidden rounded-2xl bg-gray-100 min-h-[560px]">
                <ThemeProvider>
                  <FaceLivenessDetectorCore
                    sessionId={photoCheck.session.sessionId}
                    region={photoCheck.session.region}
                    onAnalysisComplete={finishRecoveryLiveness}
                    onError={() => {
                      setError("Live check could not be completed. Please try again.");
                      setPhotoCheck(null);
                    }}
                    onUserCancel={() => setPhotoCheck(null)}
                    config={{ credentialProvider: livenessCredentials }}
                  />
                </ThemeProvider>
              </div>
            ) : (
              <>
                <p className="rounded-lg bg-amber-50 p-4 text-amber-900">
                  Your photo must be a clear photo of you and pass the existing secure photo check.
                </p>
                <label className="mt-5 flex w-full flex-col gap-2 text-sm font-semibold">
                  Profile photo
                  <input
                    aria-label="Profile photo"
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(event) => {
                      setPhoto(event.target.files?.[0] || null);
                      setPhotoCheck(null);
                      setError("");
                    }}
                  />
                </label>
                <CustomButton
                  type="button"
                  className="mt-6"
                  onClick={checkRecoveryPhoto}
                  disabled={loading || !photo}
                >
                  Check photo and continue
                </CustomButton>
              </>
            )}
          </div>
        ) : null}

        {error ? <p className="mt-5 text-red-600">{error}</p> : null}
      </div>
    </main>
  );
};
